"use client"

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, ChevronRight, CreditCard, Crown, Settings, Receipt, ArrowUpRight, Trash2, Upload, User, Shield, Calendar, ArrowLeft, Check, ArrowUp, CirclePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { NewPricingSection } from '@/components/new-pricing-section';
import { redirect } from "next/navigation";

const pricingPlans = [
    {
        id: '6d4ecf79-200c-4efc-85a9-e1205386d362', 
        name: "Free Plan",
        priceLabel: "0$ /",
        features: [
            "Capture up to 200 fans (emails/texts/socials)",
            "1 campaign per month (blast your fans with a track, merch drop, or show)",
            "Basic fan analytics (total fans, engagement rate)",
            "1 smart link / QR code (to promote your music)",
        ],
        cta: "Join for Free",
        link: "/settings",
    },
    {
        id: '6b8ff00f-be5d-4da4-b007-06b4e27a2226',
        name: "Growth Plan",
        priceLabel: "$19.00 /",
  price_cents: 1900,
  monthly: { id: '6b8ff00f-be5d-4da4-b007-06b4e27a2226', name: "Growth Plan", interval: 'month', price_cents: 1900, stripe_price_id: 'price_1S77FM4H36Uva1e4k8rSlQZI' },
  yearly: { id: '3775e19a-4d33-4d6d-b979-30a3e47ba152', name: "Growth Plan", interval: 'year', price_cents: 12000, stripe_price_id: 'price_1S77Ft4H36Uva1e4q1Id8R5d' },
        features: [
            "Everything in Free, plus:",
            "Capture up to 2,500 fans.",
            "Unlimited campaigns (email + SMS).",
            "Advanced fan analytics (top listeners, engagement by release, fan location).",
            "Custom fan tags & segmentation (separate casual listeners vs superfans).",
            "3 smart links / QR codes (multi-project promos).",
            "Priority email support.",
        ],
        cta: "Join Growth Plan",
        priceId: 'price_1S77FM4H36Uva1e4k8rSlQZI',
        popular: true,
    },
    {
        id: '2aadfdd1-ec49-4e7f-a1ce-c3801fa0180e',
        name: "Pro Plan",
        priceLabel: "$49.00 /",
  price_cents: 4900,
  monthly: { id: '2aadfdd1-ec49-4e7f-a1ce-c3801fa0180e', name: "Pro Plan", interval: 'month', price_cents: 4900, stripe_price_id: 'price_1S77Gt4H36Uva1e4d6hMADiw' },
  yearly: { id: '650b948a-00b9-4218-83e3-27afd6208f0c', name: "Pro Plan", interval: 'year', price_cents: 35000, stripe_price_id: 'price_1S77HR4H36Uva1e4tlksZpHG' },
        features: [
            "Everything in Growth, plus:",
            "Capture up to 10,000 fans.",
            "Automated fan journeys (welcome flows, follow-ups after releases, birthday/ anniversary messages).",
            "Merch + ticketing integrations (sell directly to your fans, track buyer, implemented later).",
            "Collaboration tools (cross-promos with other artists).",
            "Up to 10 smart links / QR codes.",
            "1-on-1 onboarding call.",
        ],
        cta: "Join Pro Plan",
        priceId: 'price_1S77Gt4H36Uva1e4d6hMADiw',
    },
];

const Setting = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planRow, setPlanRow] = useState(null); // dynamic plan from DB (id, stripe_price_id, price_cents, interval)
  const [credits, setCredits] = useState(0);   // referral credits available to current user
  const [discountStatus, setDiscountStatus] = useState({ hasDiscount: false, percentOff: null });
  const [allPlans, setAllPlans] = useState([]); // dynamic plans list for upgrade dialog
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const formatNextBillingDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays <= 7) {
      return `in ${diffDays} day${diffDays !== 1 ? "s" : ""}`
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    })
  }


  // Fetch user profile, subscription, and static plan mapping
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      supabase.from('profiles').select('full_name, email').eq('id', user.id).maybeSingle(),
      // include billing metadata columns that server.js writes
      supabase.from('subscriptions').select('plan_id, status, session_id, customer_id, next_billing_date, payment_method_brand, payment_method_last4, payment_method_exp_month, payment_method_exp_year, invoice').eq('user_id', user.id).maybeSingle()
    ]).then(async ([{ data: profileData }, { data: subData }]) => {
      setProfile(profileData);
      setSubscription(subData);
      if (subData?.plan_id) {
        // Try to match plan by id or name
        let planData = null;
        // Try by id
        planData = pricingPlans.find(p => p.id === subData.plan_id);
        // If not found, try by name (fallback)
        if (!planData && subData.name) {
          planData = pricingPlans.find(p => p.name === subData.name);
        }
        setPlan(planData);
      }
      setLoading(false);
    });
  }, [user]);

  // Load dynamic plan row (real price) when subscription changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!subscription?.plan_id) { setPlanRow(null); return }
      const { data, error } = await supabase
        .from('plans')
        .select('id, name, stripe_price_id, price_cents, interval')
        .eq('id', subscription.plan_id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error('Load dynamic plan error:', error);
        setPlanRow(null);
      } else {
        setPlanRow(data);
      }
    })();
    return () => { cancelled = true };
  }, [subscription?.plan_id]);

  // Fetch referral credits (for discounted offer at renewal)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setCredits(0); return }
      try {
  const res = await fetch(`https://backend.amply.buildfast.us/referral/credits/${user.id}`);
        if (!res.ok) throw new Error(`credits http ${res.status}`);
        const json = await res.json();
        if (!cancelled) setCredits(json?.credits || 0);
      } catch (e) {
        console.warn('Could not load referral credits:', e);
        if (!cancelled) setCredits(0);
      }
    })();
    return () => { cancelled = true };
  }, [user?.id]);

  // Load live discount status from Stripe via backend (if user has active sub)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!user) { setDiscountStatus({ hasDiscount: false, percentOff: null }); return }
      try {
  const r = await fetch(`https://backend.amply.buildfast.us/subscription/discount-status/${encodeURIComponent(user.id)}`)
        const j = await r.json()
        if (!cancelled) setDiscountStatus({ hasDiscount: !!j?.hasDiscount, percentOff: j?.percentOff ?? null })
      } catch (e) {
        if (!cancelled) setDiscountStatus({ hasDiscount: false, percentOff: null })
      }
    })()
    return () => { cancelled = true }
  }, [user?.id])

  // Load all plans for upgrade dialog
  useEffect(() => {
    let cancelled = false
    supabase
      .from('plans')
      .select('*')
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) console.error('Error loading plans:', error)
        else setAllPlans(data || [])
      })
    return () => { cancelled = true }
  }, [])

  // Compute display prices (supports yearly interval + active discount, show original + discounted simultaneously with full labels)
  const { priceLabel, originalPrice, discountedPrice } = useMemo(() => {
    const cents = planRow?.price_cents;
    if (cents == null) {
      return { priceLabel: plan?.priceLabel || '$0', originalPrice: null, discountedPrice: null };
    }
    const baseAmount = (cents / 100).toFixed(2);
    const isYear = planRow.interval === 'year';
    const perMonth = isYear ? (cents / 12 / 100).toFixed(2) : null;
    const computedLabel = isYear
      ? `$${baseAmount} / year (~$${perMonth}/mo)`
      : `$${baseAmount} / month`;

    const hasActiveDiscount = discountStatus.hasDiscount && cents > 0;
    const percentOff = (typeof discountStatus.percentOff === 'number') ? discountStatus.percentOff : 50;
    const discountedRaw = hasActiveDiscount
      ? (Math.max(0, cents * (1 - percentOff / 100)) / 100).toFixed(2)
      : null;

    let originalDisplay = null;
    let discountedDisplay = null;
    if (hasActiveDiscount) {
      if (isYear) {
        // Show original yearly with suffix, discounted first-year incl. per-month equivalent
        originalDisplay = `$${baseAmount} / year`;
        discountedDisplay = `$${discountedRaw} / first year (~$${perMonth}/mo)`;
      } else {
        originalDisplay = `$${baseAmount} / month`;
        discountedDisplay = `$${discountedRaw} / first month`;
      }
    }
    return {
      priceLabel: computedLabel,
      originalPrice: originalDisplay,
      discountedPrice: discountedDisplay,
    };
  }, [planRow, discountStatus, plan?.priceLabel]);

  // Determine if user currently has a paid subscription (monthly or yearly) even if static mapping failed.
  const hasPaidSubscription = useMemo(() => {
    if (!subscription) return false;
    // If dynamic plan row loaded, trust its price_cents
    if (planRow && typeof planRow.price_cents === 'number') return planRow.price_cents > 0;
    // Fallback: use static plan lookup by name (covers yearly where id differs but name same)
    if (plan?.name && plan.name !== 'Free Plan') return true;
    return false;
  }, [subscription, planRow, plan]);

  const handleManagePlan = async () => {
    if (!subscription?.session_id) return alert('No active subscription session found.');
    setPortalLoading(true);
    try {
  const res = await fetch('https://backend.amply.buildfast.us/create-customer-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: subscription.session_id })
      });
      const { url, error } = await res.json();
      if (error) alert(error);
      else window.location.href = url;
    } finally {
      setPortalLoading(false);
    }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-gray-500">Please log in.</div>;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;

  return (
    <>
    <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
  <DialogContent className="backdrop-blur-2xl bg-black/70 dark:bg-black/70 supports-[backdrop-filter]:bg-black/50 dark:supports-[backdrop-filter]:bg-black/60 border border-white/20 dark:border-white/10 shadow-2xl max-w-[80%] max-h-[100%]">
      <NewPricingSection plans={allPlans} />
          <DialogFooter>
            <Button className="text-black dark:text-white" variant="outline" onClick={() => setShowUpgradeDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="min-h-screen bg-white dark:bg-black">
        <div className="max-w-6xl mx-auto p-8">
          {/* Account Settings Section */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 rounded-lg  bg-white dark:bg-black">
                <User className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Account Settings</h1>
                <p className="text-gray-600 dark:text-gray-400">View information about your account</p>
              </div>
            </div>
            {/* Form Fields */}
            <div className="space-y-6 max-w-2xl">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  value={profile?.full_name || ''}
                  readOnly
                  className="bg-gray-100 dark:bg-black shadow-sm border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profile?.email || ''}
                  readOnly
                  className="bg-gray-100 dark:bg-black shadow-sm border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>
          {/* Plan & Billing Section */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 rounded-lg  bg-white dark:bg-black">
                <CreditCard className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Plan & Billing</h2>
                <p className="text-gray-600 dark:text-gray-400">Manage your subscription and billing information</p>
              </div>
            </div>
            <div className="space-y-8 max-w-4xl"> 
   <Card className="border-gray-200 dark:border-gray-700 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 dark:from-yellow-400/5 dark:to-orange-400/5 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                  <CardTitle className="text-lg text-black dark:text-white">Current Plan</CardTitle>
                </div>
                {plan && (
                  <Badge
                    variant="secondary"
                    className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
                  >
                    Active
                  </Badge>
                )}
              </div>
              <CardDescription className="text-black dark:text-white">
                {plan ? `You are currently subscribed to ${plan.name}` : "No active subscription"}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-black dark:text-white relative space-y-4 ">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">{plan?.name || planRow?.name || "Free Plan"}</h3>
                <div className="flex items-baseline gap-3 flex-wrap">
          {originalPrice && discountedPrice ? (
                    <>
                      <span className="text-2xl line-through opacity-70">{originalPrice}</span>
                      <span className="text-3xl font-bold">{discountedPrice}</span>
            <span className="text-muted-foreground text-sm"> {planRow?.interval === 'year' ? 'first year (50% off)' : 'next renewal (50% off)'}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold">{priceLabel}</span>
                    </>
                  )}
                </div>
                {credits > 0 && planRow?.price_cents > 0 && (
                  <div className="mt-1 text-sm text-green-600 dark:text-green-400">You have {credits} referral credit{credits>1? 's':''}. Apply one to get 50% off a month.</div>
                )}
                {subscription?.status && (
                  <div className="flex items-center gap-2 text-sm">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        subscription.status === "active"
                          ? "bg-green-500 dark:bg-green-400"
                          : subscription.status === "past_due"
                            ? "bg-yellow-500 dark:bg-yellow-400"
                            : "bg-red-500 dark:bg-red-400"
                      }`}
                    />
                    <span className="capitalize text-muted-foreground">Status: {subscription.status}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <Button className="w-full border border-b/60" onClick={() => window.location.replace('./pricing')}>
                  <CirclePlus className="mr-2 h-4 w-4" />
                  Upgrade Plan
                </Button>

                {hasPaidSubscription && (
                  <Button
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={handleManagePlan}
                    disabled={portalLoading}
                  >
                    {portalLoading ? "Loading..." : "Manage Subscription"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Billing Information Card (only show if user has a paid subscription) */}
          {subscription && hasPaidSubscription && (
            <Card className="border-gray-200 dark:border-gray-700">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-500" />
                  <CardTitle className="text-lg text-black dark:text-white">Billing Information</CardTitle>
                </div>
                <CardDescription className="text-black dark:text-white">Payment method and billing details</CardDescription>
              </CardHeader>
              <CardContent className="text-black dark:text-white space-y-4">
                {/* Payment Method */}
                {(subscription?.payment_method_brand || subscription?.payment_method_last4) && (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-500 rounded flex items-center justify-center">
                        <CreditCard className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {subscription.payment_method_brand?.toUpperCase() || "Card"}
                          {subscription.payment_method_last4 && ` •••• ${subscription.payment_method_last4}`}
                        </p>
                        <p className="text-sm text-muted-foreground">Primary payment method</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Next Billing Date */}
                {subscription?.next_billing_date && (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 dark:from-green-400 dark:to-emerald-500 rounded flex items-center justify-center">
                        <Calendar className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">
                          Next billing {formatNextBillingDate(subscription.next_billing_date)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(subscription.next_billing_date).toLocaleDateString(undefined, {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                        {discountedPrice && (
                          <p className="text-sm text-green-600 dark:text-green-400 mt-1">With a referral credit applied: {discountedPrice}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col gap-2 pt-2">
                  {subscription?.invoice && (
                    <Button
                      variant="outline"
                      className="w-full justify-between bg-transparent"
                      onClick={() => window.open(subscription.invoice, "_blank")}
                    >
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4" />
                        View Latest Invoice
                      </div>
                      <ArrowUpRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
              {/* Plan Features */}
              {(plan || !plan) && (
                <Card className=" bg-white dark:bg-black border-gray-200 dark:border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-900 dark:text-gray-100">Plan Features</CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-400">
                      What's included in your {plan?.name || 'Free Plan'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {((plan && plan.features) || pricingPlans[0].features).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <Check className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Setting;
